import { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../api";

const TODAY = () => new Date().toISOString().slice(0, 10);

/**
 * State + side-effects for the single-payment registration modal.
 * Loads payment types + fresh client position on mount; exposes form
 * state and a `submit()` action that registers and refreshes.
 */
export default function usePaymentRegister(client, onRegistered) {
  const [types, setTypes] = useState([]);
  const [position, setPosition] = useState(null);
  const [payments, setPayments] = useState([]);
  const [data, setData] = useState(TODAY());
  const [valor, setValor] = useState(Number(client?.PorPagar || 0).toFixed(2));
  const [tipo, setTipo] = useState("");
  const [obs, setObs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const clientId = client?.ID_Cliente;

  // Load payment types + fresh position on mount (per client)
  useEffect(() => {
    if (!clientId) return;
    Promise.all([
      adminApi.opsPaymentTypes().catch(() => ({ data: [] })),
      adminApi.opsCommissionPosition(clientId).catch(() => null),
    ]).then(([tps, pos]) => {
      const items = tps?.data || [];
      setTypes(items);
      if (items.length) setTipo((cur) => cur || String(items[0].id));
      if (pos) {
        setPosition(pos.position || null);
        setPayments(pos.payments || []);
        if (pos.position?.PorPagar !== undefined) {
          setValor(Number(pos.position.PorPagar || 0).toFixed(2));
        }
      }
    });
  }, [clientId]);

  const refreshPosition = useCallback(async () => {
    if (!clientId) return;
    try {
      const pos = await adminApi.opsCommissionPosition(clientId);
      setPosition(pos.position || null);
      setPayments(pos.payments || []);
      setValor(Number(pos.position?.PorPagar || 0).toFixed(2));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("usePaymentRegister: refreshPosition failed", e);
      }
    }
  }, [clientId]);

  const submit = useCallback(async () => {
    setErr(""); setOk("");
    const v = Number(valor);
    if (!v || Number.isNaN(v)) { setErr("Valor inválido (não pode ser zero)."); return; }
    if (!tipo) { setErr("Escolhe o tipo de pagamento."); return; }
    if (!data) { setErr("Data obrigatória."); return; }
    setSubmitting(true);
    try {
      const r = await adminApi.opsRegisterCommissionPayment({
        client_id: clientId,
        valor_pago: v,
        id_tipo_pagamento: tipo,
        data,
        observacoes: obs || null,
      });
      setOk(r.message || "Pagamento registado.");
      await refreshPosition();
      setObs("");
      onRegistered?.();
    } catch (e) {
      setErr(e.message || "Erro a registar pagamento.");
    } finally {
      setSubmitting(false);
    }
  }, [clientId, valor, tipo, data, obs, refreshPosition, onRegistered]);

  return {
    // form state
    data, setData, valor, setValor, tipo, setTipo, obs, setObs,
    // server state
    types, position, payments,
    // submit
    submit, submitting, err, ok,
  };
}
